import { z } from 'zod';

import type {
  NotificationTemplate,
  PutAdminV1StoresStoreIdNotificationTemplatesEventBody,
} from '@nucleus-commerce/api-client/admin';

import { toOverrideText, unknownPlaceholders } from './placeholder-rules';

/** Refuses placeholders the event does not know, the way the backend would with a 422. */
export function templateFormSchema(allowed: readonly string[]) {
  const text = (max: number) =>
    z
      .string()
      .max(max, { error: 'notifications:validation.tooLong' })
      .refine((value) => unknownPlaceholders(value, allowed).length === 0, {
        error: 'notifications:validation.unknownPlaceholder',
      });
  // The backend's TemplateText caps the subject at 200 and the body at 10,000.
  return z.object({ subject: text(200), body: text(10_000), isEnabled: z.boolean() });
}

export type TemplateForm = z.infer<ReturnType<typeof templateFormSchema>>;

export function fromTemplate(template: NotificationTemplate): TemplateForm {
  return {
    subject: template.override?.subject ?? '',
    body: template.override?.body ?? '',
    isEnabled: template.isEnabled,
  };
}

/** Blank text, or text equal to the default wording, means "use the default": the API gets null. */
export function toTemplateBody(
  values: TemplateForm,
  defaults: NotificationTemplate['defaultTemplate'],
): PutAdminV1StoresStoreIdNotificationTemplatesEventBody {
  const own = (value: string, fallback: string) =>
    value.trim() === fallback.trim() ? null : toOverrideText(value);
  return {
    subject: own(values.subject, defaults.subject),
    body: own(values.body, defaults.body),
    isEnabled: values.isEnabled,
  };
}
